import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, X, FolderUp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DOC_TYPES = [
  { value: 'national_id', label: 'National ID / Passport' },
  { value: 'company_registration', label: 'Company Registration' },
  { value: 'agreement', label: 'Signed Agreement' },
  { value: 'british_council', label: 'British Council' },
  { value: 'accreditation', label: 'Accreditation' },
  { value: 'bank_proof', label: 'Bank Proof' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' },
];

const ALLOWED_EXT = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const BulkAgentDocumentRecovery = ({ preFilterAgentId = '' }) => {
  const [items, setItems] = useState([]); // {file, name, agent_id, doc_type, agent_name, score, needs_review, ext_ok, status, message}
  const [matching, setMatching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [agentSearch, setAgentSearch] = useState({});
  const [pinnedAgent, setPinnedAgent] = useState(null); // {id, name, missing[]}
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const cardRef = useRef(null);

  // When the URL deep-links us to a specific agent (Agents tab "Missing X docs" badge),
  // pin them at the top so every new dropped file auto-targets that agent.
  useEffect(() => {
    if (!preFilterAgentId) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/admin/agents/compliance-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const a = (r.data?.agents || []).find(x => x.id === preFilterAgentId);
        if (a) {
          setPinnedAgent({ id: a.id, name: a.company_name || a.full_name, missing: a.missing || [] });
        } else {
          setPinnedAgent({ id: preFilterAgentId, name: 'Selected agent', missing: [] });
        }
        // Scroll the recovery panel into view smoothly so the admin lands on it.
        setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
      })
      .catch(() => {
        setPinnedAgent({ id: preFilterAgentId, name: 'Selected agent', missing: [] });
      });
  }, [preFilterAgentId]);

  const handleFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter(f => f && f.size > 0);
    if (incoming.length === 0) return;
    if (items.length + incoming.length > 50) {
      toast.error('Max 50 files per batch');
      return;
    }
    const filenames = incoming.map(f => f.name);
    setMatching(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/agents/bulk-documents/match`,
        { filenames },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      const matches = data?.matches || [];
      const newItems = incoming.map((f, i) => {
        const m = matches[i] || {};
        // If the panel is pinned to a specific agent (deep-linked from Agents tab),
        // every new file targets THAT agent and auto-fills the missing doc-type
        // when there's only one obvious choice.
        const usePinned = !!pinnedAgent;
        const guessedType = m.guessed_doc_type || 'other';
        const pinnedType = usePinned && pinnedAgent.missing?.length === 1
          ? pinnedAgent.missing[0]
          : guessedType;
        return {
          file: f,
          name: f.name,
          size: f.size,
          ext_ok: m.ext_ok ?? ALLOWED_EXT.some(e => f.name.toLowerCase().endsWith(e)),
          agent_id: usePinned ? pinnedAgent.id : (m.matched_agent_id || ''),
          agent_name: usePinned ? pinnedAgent.name : (m.matched_agent_name || ''),
          doc_type: usePinned ? pinnedType : guessedType,
          score: usePinned ? 999 : (m.match_score || 0),
          needs_review: usePinned ? false : (m.needs_review !== false ? !m.matched_agent_id : false),
          status: 'pending',
          message: '',
        };
      });
      setItems(prev => [...prev, ...newItems]);
      const unmatched = newItems.filter(i => !i.agent_id).length;
      toast.success(`${newItems.length} file${newItems.length > 1 ? 's' : ''} added · ${unmatched} need review`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not analyse filenames'));
    } finally {
      setMatching(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove('border-teal-500', 'bg-teal-50', 'dark:bg-teal-950/30');
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.add('border-teal-500', 'bg-teal-50', 'dark:bg-teal-950/30');
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove('border-teal-500', 'bg-teal-50', 'dark:bg-teal-950/30');
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const clearAll = () => setItems([]);

  const searchAgents = async (idx, query) => {
    setAgentSearch(prev => ({ ...prev, [idx]: { ...prev[idx], query, loading: true } }));
    try {
      const { data } = await axios.get(`${API_URL}/api/agents`, { headers: authHeader() });
      const list = (Array.isArray(data) ? data : data?.agents || []);
      const q = query.toLowerCase();
      const filtered = list.filter(a =>
        (a.company_name || '').toLowerCase().includes(q) ||
        (a.full_name || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q),
      ).slice(0, 8);
      setAgentSearch(prev => ({ ...prev, [idx]: { query, loading: false, results: filtered } }));
    } catch (e) {
      setAgentSearch(prev => ({ ...prev, [idx]: { query, loading: false, results: [] } }));
    }
  };

  const upload = async () => {
    const ready = items.filter(i => i.agent_id && i.doc_type && i.ext_ok && i.status !== 'ok');
    if (ready.length === 0) {
      toast.error('No files ready — assign an agent and document type first.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      ready.forEach((i) => {
        fd.append('files', i.file, i.name);
        fd.append('agent_ids', i.agent_id);
        fd.append('doc_types', i.doc_type);
        fd.append('doc_names', i.name);
      });
      const { data } = await axios.post(
        `${API_URL}/api/admin/agents/bulk-documents/upload`,
        fd,
        { headers: { ...authHeader() } },
      );
      const results = data?.results || [];
      // Apply results back onto the items list by filename.
      setItems(prev => prev.map(it => {
        if (it.status === 'ok') return it;
        const r = results.find(x => x.filename === it.name && x.agent_id === it.agent_id);
        if (!r) return it;
        return { ...it, status: r.status, message: r.message, document_id: r.document_id };
      }));
      const ok = data?.success_count ?? results.filter(r => r.status === 'ok').length;
      if (ok === ready.length) toast.success(`All ${ok} files uploaded successfully`);
      else toast(`${ok}/${ready.length} files uploaded — see per-row status`, { description: 'Failed rows show the reason inline.' });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Bulk upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const readyCount = items.filter(i => i.agent_id && i.doc_type && i.ext_ok && i.status !== 'ok').length;
  const reviewCount = items.filter(i => !i.agent_id || !i.ext_ok).length;
  const okCount = items.filter(i => i.status === 'ok').length;

  return (
    <Card data-testid="bulk-agent-doc-recovery" ref={cardRef}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FolderUp className="w-5 h-5 text-teal-600" />
          Bulk Agent Document Recovery
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Drop multiple agent documents — we'll auto-match them to the right agent + document type by filename.
          Review the suggestions, then upload them all at once. Files persist to Object Storage and survive redeploys.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pinned-agent banner — set when deep-linked from Agents tab */}
        {pinnedAgent && (
          <div className="flex items-center justify-between p-3 rounded-lg border border-teal-200 bg-teal-50 dark:bg-teal-950/30 dark:border-teal-900" data-testid="bulk-doc-pinned-agent">
            <div className="text-sm text-teal-800 dark:text-teal-200">
              <span className="font-semibold">Recovering for:</span>{' '}
              <span className="font-medium">{pinnedAgent.name}</span>
              {pinnedAgent.missing?.length > 0 && (
                <span className="ml-2 text-xs text-teal-700 dark:text-teal-300">
                  · still missing {pinnedAgent.missing.map(m => DOC_TYPES.find(d => d.value === m)?.label || m).join(', ')}
                </span>
              )}
              <p className="text-xs text-teal-700 dark:text-teal-300 mt-1">
                Every file you drop here will be assigned to this agent automatically.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPinnedAgent(null)} className="h-8" data-testid="bulk-doc-clear-pin">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        )}
        {/* Drop zone */}
        <div
          ref={dropRef}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-teal-400 transition-colors"
          data-testid="bulk-doc-dropzone"
        >
          <UploadCloud className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Drop files here or click to browse
          </p>
          <p className="text-xs text-gray-500 mt-1">
            PDF, JPG, PNG, DOC, DOCX · up to 50 files per batch
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ALLOWED_EXT.join(',')}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="bulk-doc-file-input"
          />
        </div>

        {matching && <p className="text-sm text-gray-500">Analysing filenames…</p>}

        {/* Summary + actions */}
        {items.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">{readyCount} ready</Badge>
              {reviewCount > 0 && (
                <Badge className="bg-amber-500 text-white hover:bg-amber-500">{reviewCount} need review</Badge>
              )}
              {okCount > 0 && (
                <Badge className="bg-blue-500 text-white hover:bg-blue-500">{okCount} uploaded</Badge>
              )}
              <span className="text-gray-500">· {items.length} total</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={clearAll} disabled={uploading} data-testid="bulk-doc-clear-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Clear
              </Button>
              <Button onClick={upload} disabled={uploading || readyCount === 0} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="bulk-doc-upload-btn">
                <UploadCloud className={`w-4 h-4 mr-1 ${uploading ? 'animate-pulse' : ''}`} />
                {uploading ? 'Uploading…' : `Upload ${readyCount} file${readyCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}

        {/* Per-file rows */}
        {items.length > 0 && (
          <div className="space-y-2 max-h-[28rem] overflow-y-auto">
            {items.map((it, idx) => {
              const isOk = it.status === 'ok';
              const isError = it.status === 'error';
              const search = agentSearch[idx] || {};
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${isOk ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20' : isError ? 'border-rose-200 bg-rose-50/40 dark:bg-rose-950/20' : it.needs_review ? 'border-amber-200 bg-amber-50/30 dark:bg-amber-950/10' : 'border-gray-200 dark:border-slate-700'}`}
                  data-testid={`bulk-doc-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{it.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatBytes(it.size)}
                          {!it.ext_ok && <span className="text-rose-600 ml-2">· unsupported file type</span>}
                          {it.score > 0 && <span className="ml-2">· match score {it.score}</span>}
                        </p>
                        {it.message && (
                          <p className={`text-xs mt-1 ${isOk ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isOk ? <CheckCircle2 className="inline w-3 h-3 mr-1" /> : <AlertTriangle className="inline w-3 h-3 mr-1" />}
                            {it.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {!isOk && (
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-8 w-8 p-0" data-testid={`bulk-doc-remove-${idx}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {!isOk && it.ext_ok && (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Agent selector with search */}
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Agent</label>
                        {it.agent_id ? (
                          <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700">
                            <span className="text-sm truncate">{it.agent_name}</span>
                            <button
                              type="button"
                              onClick={() => updateItem(idx, { agent_id: '', agent_name: '' })}
                              className="text-xs text-gray-500 hover:text-rose-600 ml-2"
                              data-testid={`bulk-doc-change-agent-${idx}`}
                            >
                              change
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <Input
                              placeholder="Search agent by name, company, or email…"
                              value={search.query || ''}
                              onChange={(e) => searchAgents(idx, e.target.value)}
                              className="text-sm"
                              data-testid={`bulk-doc-search-${idx}`}
                            />
                            {search.query && search.results && (
                              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-lg max-h-40 overflow-y-auto">
                                {search.loading && <div className="p-2 text-xs text-gray-500">Loading…</div>}
                                {!search.loading && search.results.length === 0 && (
                                  <div className="p-2 text-xs text-gray-500">No agents match.</div>
                                )}
                                {!search.loading && search.results.map(a => (
                                  <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => {
                                      updateItem(idx, { agent_id: a.id, agent_name: a.company_name || a.full_name });
                                      setAgentSearch(prev => ({ ...prev, [idx]: {} }));
                                    }}
                                    className="w-full text-left p-2 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm"
                                  >
                                    <div className="font-medium">{a.company_name || a.full_name}</div>
                                    <div className="text-xs text-gray-500">{a.email}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Doc type */}
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Document Type</label>
                        <Select value={it.doc_type} onValueChange={(v) => updateItem(idx, { doc_type: v })}>
                          <SelectTrigger className="text-sm" data-testid={`bulk-doc-type-${idx}`}>
                            <SelectValue placeholder="Pick type" />
                          </SelectTrigger>
                          <SelectContent>
                            {DOC_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkAgentDocumentRecovery;
